import { useCallback, useMemo } from "react";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, LogOut, Menu, User as UserIcon, X } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import Avatar from "components/shared/Avatar";
import Button from "components/shared/Button";
import Select from "components/shared/inputs/Select";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useUser } from "utils/hooks/user";
import { useCompany } from "utils/hooks/company";
import { useAccess } from "utils/hooks/access";
import { useAuthentication } from "features/Authentication/context";
import { useImpersonate } from "features/People/api";
import { useLogout } from "features/Authentication/api";
import useApi from "utils/api";
import { renderCompanyResourceLabel } from "utils/helpers/settings";
import { adaptMomentToCompany } from "utils/helpers/adaptMomentToCompany";
import { getErrorMessage } from "utils/helpers/errors";
import SearchInput from "./SearchInput";
import Notifications from "./Notifications";
import Messages from "./Messages";
import getEnTranslate from "utils/helpers/moment/getEnTranslate";
import getEsTranslate from "utils/helpers/moment/getEsTranslate";
import { cn } from "lib/utils";
import "./style.scss";

const LANGUAGES: { code: "en" | "es"; flag: string; locale: () => moment.Locale }[] = [
    { code: "en", flag: "fi fi-us", locale: getEnTranslate },
    { code: "es", flag: "fi fi-es", locale: getEsTranslate },
];

type NavbarProps = {
    theme?: "dark" | "light";
    sidenavOpen?: boolean;
    toggleSidenav?: () => void;
};

function Navbar({ theme = "dark", sidenavOpen = false, toggleSidenav = () => { } }: NavbarProps) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const { hasAccess: canViewProfile } = useAccess("profile.canView");
    const user = useUser();
    const company = useCompany();
    const { logout } = useLogout();
    const { dispatch } = useAuthentication();
    const { logout: impersonateLogout } = useImpersonate();
    const { authPut } = useApi();

    const impersonate = localStorage.getItem("IMPERSONATE");
    const language = localStorage.getItem("i18nextLng") || "en";

    const setUserLng = useCallback(
        async (lng: string) => {
            try {
                return await authPut("/profile/set-language", { data: { language: lng } });
            } catch (error) {
                getErrorMessage(error, t);
            }
        },
        [authPut, t]
    );

    const changeLanguage = useCallback(
        (lng: string, locale: () => moment.Locale) => {
            localStorage.setItem("i18nextLng", lng);
            setUserLng(lng);
            i18n.changeLanguage(lng);
            moment.updateLocale(lng, locale() as unknown as moment.LocaleSpecification);
        },
        [i18n, setUserLng]
    );

    const activeLanguage = useMemo(
        () => LANGUAGES.find(({ code }) => code === language) || LANGUAGES[0],
        [language]
    );

    const onCompanyChange = useCallback(
        (id: string) => {
            const nextCompany = user?.companies?.find((item) => item.id === id);
            if (nextCompany) {
                localStorage.setItem("COMPANY", nextCompany.id);
                adaptMomentToCompany(nextCompany, user);
                dispatch({ type: "set-company", payload: { company: nextCompany } });
                navigate("/");
            }
        },
        [dispatch, user, navigate]
    );

    const logOut = useCallback(() => {
        if (impersonate) {
            impersonateLogout(() => navigate("/"));
        } else {
            logout();
            localStorage.removeItem("i18nextLng");
            localStorage.removeItem("OTP_VALIDATE");
            if (company) {
                adaptMomentToCompany(company, user);
            }
        }
    }, [impersonate, company, user, impersonateLogout, logout, navigate]);

    const isDemoEnvironment = process.env.REACT_APP_IS_DEMO === "true";
    const isTestEnvironment = process.env.REACT_APP_ENV === "development" && !isDemoEnvironment;

    const navbarClass = isTestEnvironment
        ? "navbar-dark bg-purple"
        : theme === "dark"
            ? "navbar-dark bg-info"
            : "navbar-light bg-secondary";

    return (
        <div
            className={cn(
                "admin-navbar",
                navbarClass,
                "tw-relative tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-4 md:tw-px-6"
            )}
        >
            {isMobile && (
                <Button
                    variant="ghost"
                    size="icon"
                    className={cn("tw-shrink-0 tw-text-white", theme !== "dark" && "tw-text-foreground")}
                    onClick={toggleSidenav}
                >
                    {sidenavOpen ? <X /> : <Menu />}
                </Button>
            )}

            <div className="tw-min-w-0 tw-flex-1">
                <SearchInput />
            </div>

            {isTestEnvironment && (
                <div className="tw-ml-auto tw-hidden tw-shrink-0 tw-text-xl tw-font-bold tw-uppercase tw-text-white md:tw-block">
                    {t("test-environment")}
                </div>
            )}

            <div
                className={cn(
                    "tw-ml-auto tw-flex tw-shrink-0 tw-items-center tw-gap-4",
                    theme === "dark" ? "tw-text-white" : "tw-text-foreground"
                )}
            >
                {user?.companies && user.companies.length > 1 && (
                    <Select
                        value={company?.id}
                        onChange={(id) => id && onCompanyChange(id)}
                        className="tw-w-70 tw-bg-white tw-text-neutral-800"
                    >
                        {user.companies.map((item) => (
                            <Select.Item key={item.id} value={item.id}>
                                {renderCompanyResourceLabel(item)}
                            </Select.Item>
                        ))}
                    </Select>
                )}

                <Notifications />
                <Messages />

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button className="tw-flex tw-items-center tw-gap-2 tw-outline-none">
                            <Avatar
                                user={user}
                                className="tw-h-10 tw-w-10"
                                textStyles="tw-text-neutral-700 tw-font-bold"
                            />
                            <span
                                className={cn(
                                    "tw-hidden tw-text-sm tw-font-semibold lg:tw-inline",
                                    theme === "dark" ? "tw-text-white" : "tw-text-foreground"
                                )}
                            >
                                {`${user?.firstName} ${user?.lastName}`}
                            </span>
                        </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end" className="tw-w-56">
                        <DropdownMenuLabel>{t("welcome")}!</DropdownMenuLabel>

                        {canViewProfile && (
                            <DropdownMenuItem onClick={() => navigate("/profile/job-pay")}>
                                <UserIcon />
                                <span>{t("my-profile")}</span>
                            </DropdownMenuItem>
                        )}

                        <DropdownMenuSeparator />

                        <DropdownMenuItem onClick={logOut}>
                            <LogOut />
                            <span>{t("logout")}</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className={cn(
                                "tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-semibold tw-uppercase tw-outline-none",
                                theme === "dark" ? "tw-text-white" : "tw-text-foreground"
                            )}
                        >
                            <span className={activeLanguage.flag} />
                            <ChevronDown className="tw-size-3.5" />
                        </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end">
                        {LANGUAGES.map(({ code, flag, locale }) => (
                            <DropdownMenuItem key={code} onClick={() => changeLanguage(code, locale)}>
                                <span className={flag} />
                                <span>{t(code)}</span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <div className={cn("navbar-bottom-background", isTestEnvironment && "bg-purple")} />
        </div>
    );
}

export default Navbar;
